import { useCallback, useId, useState } from 'react'
import { Selector } from '@Components/selector/selector'
import { FormFloatingFooter } from '@Components/claim-flow/form-floating-footer'
import { Button } from '@Components/button/button'
import { getFieldEnum } from '../../schema-utils'
import { formatPrizeDisplay } from '../../format-utils'
import { GameTypeHelpModal } from './game-type-help-modal'
import { BarcodeFormat, StepProps } from '@Components/claim-form-flow/types'
import { PreviousButton } from '@Components/previous-button/previous-button'

import './tickets-step.scss'

const CLASS_NAME = 'tickets-step'
const PRIZE_DIGIT_CAP = 12

type TicketEntry = {
  prizeAmount: string
  gameType: string
  barcodeNumber: string
  selectedFormatIndex: number
}

type StoredTicket = {
  prizeAmount?: string | number
  gameType?: string
  barcodeNumber?: string
  selectedFormatIndex?: number
}

function emptyTicket(): TicketEntry {
  return { prizeAmount: '', gameType: '', barcodeNumber: '', selectedFormatIndex: 0 }
}

type TicketGroupName = {
  labelledBy?: string
  label?: string
}

/** A single ticket shows no heading, so its group carries the name the heading would have given it. */
function ticketGroupName(index: number, headingId: string, hasHeading: boolean): TicketGroupName {
  if (hasHeading) {
    return { labelledBy: headingId }
  }
  return { label: `Ticket ${index + 1}` }
}

/**
 * Auto-format a barcode value based on the placeholder template.
 * The placeholder defines the shape (e.g., "1748-021474054-175190").
 * Digits are extracted from input and dashes inserted at positions
 * where the placeholder has a dash.
 */
function formatBarcode(value: string, placeholder: string): string {
  const digits = value.replace(/\D/g, '')
  let result = ''
  let digitIndex = 0

  for (let i = 0; i < placeholder.length && digitIndex < digits.length; i++) {
    if (placeholder[i] === '-') {
      result += '-'
    } else {
      result += digits[digitIndex]
      digitIndex++
    }
  }

  return result
}

export const TicketsStep = ({
  schema,
  formData,
  onSubmit,
  onBack,
  showBack = true,
  claimReservationId,
}: StepProps) => {
  const existingTickets = ((formData.tickets ?? []) as StoredTicket[]).map((t) => ({
    prizeAmount: String(t.prizeAmount ?? ''),
    gameType: t.gameType ?? '',
    barcodeNumber: t.barcodeNumber ?? '',
    selectedFormatIndex: t.selectedFormatIndex ?? 0,
  }))
  const existingPaymentMethod = (formData.paymentMethod ?? '') as string
  const fieldId = useId()

  const [numberOfTickets, setNumberOfTickets] = useState<number>(
    existingTickets.length > 0 ? existingTickets.length : 1,
  )
  const [tickets, setTickets] = useState<TicketEntry[]>(
    existingTickets.length > 0 ? existingTickets : [emptyTicket()],
  )
  const [paymentMethod, setPaymentMethod] = useState<string>(existingPaymentMethod)
  const [showGameTypeHelp, setShowGameTypeHelp] = useState(false)
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)

  const paymentMethodOptions =
    getFieldEnum(schema, '', 'paymentMethod') ?? schema.properties.paymentMethod?.enum ?? []
  const paymentMethodMaxPrize = schema.properties.paymentMethod?.['x-enum-max-prize-amount'] ?? {}
  const paymentMethodDescriptions = schema.properties.paymentMethod?.['x-enum-descriptions'] ?? {}
  const ticketProperties = schema.properties.tickets?.items?.properties ?? {}
  const gameTypeOptions = ticketProperties.gameType?.enum ?? []
  const ticketBarcodeFormats = schema.ticketBarcodeFormats ?? []
  const getBarcodeFormats = (gameType: string): BarcodeFormat[] =>
    ticketBarcodeFormats.find((entry) => entry.label === gameType)?.formats ?? []
  const isMultiFormat = (gameType: string): boolean => getBarcodeFormats(gameType).length > 1
  const getFirstFormat = (gameType: string): BarcodeFormat | undefined =>
    getBarcodeFormats(gameType)[0]

  const hasPaymentMethod = 'paymentMethod' in schema.properties
  const hasGameType = 'gameType' in ticketProperties
  const hasPrizeAmount = 'prizeAmount' in ticketProperties
  const hasBarcodeNumber = 'barcodeNumber' in ticketProperties

  const ticketRequired = schema.properties.tickets?.items?.required ?? []
  const isGameTypeRequired = ticketRequired.includes('gameType')
  const isPrizeAmountRequired = ticketRequired.includes('prizeAmount')
  const isBarcodeRequired = ticketRequired.includes('barcodeNumber')

  const isBarcodeValid = (ticket: TicketEntry): boolean => {
    if (!hasBarcodeNumber || !isBarcodeRequired) {
      return true
    }
    if (ticket.barcodeNumber === '') {
      return false
    }
    const formats = getBarcodeFormats(ticket.gameType)
    if (formats.length === 0) {
      return true
    }
    const selected = formats[ticket.selectedFormatIndex]
    return selected ? new RegExp(selected.pattern).test(ticket.barcodeNumber) : false
  }

  const handleNumberOfTicketsChange = useCallback((value: number) => {
    setNumberOfTickets(value)
    setTickets((prev) => {
      if (value > prev.length) {
        const additional = Array.from({ length: value - prev.length }, () => emptyTicket())
        return [...prev, ...additional]
      }
      return prev.slice(0, value)
    })
  }, [])

  const updateTicket = useCallback(
    (index: number, field: keyof TicketEntry, value: string | number) => {
      setTickets((prev) => {
        const updated = [...prev]
        updated[index] = { ...updated[index], [field]: value }
        return updated
      })
    },
    [],
  )

  const paymentMethodCap = paymentMethodMaxPrize[paymentMethod]

  const handlePrizeChange = useCallback(
    (index: number, raw: string) => {
      let cleaned = raw.replace(/[^0-9.]/g, '')
      const firstDot = cleaned.indexOf('.')
      if (firstDot !== -1) {
        cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '')
        if (cleaned.length - firstDot - 1 > 2) {
          cleaned = cleaned.slice(0, firstDot + 3)
        }
      }
      const digitCount = cleaned.replace(/\./g, '').length
      if (digitCount > PRIZE_DIGIT_CAP) {
        return
      }
      updateTicket(index, 'prizeAmount', cleaned)
    },
    [updateTicket],
  )

  const ticketExceedsPaymentMethodCap = (ticket: TicketEntry): boolean =>
    paymentMethodCap !== undefined && (Number(ticket.prizeAmount) || 0) > paymentMethodCap
  const exceedsPaymentMethodCap = tickets.some(ticketExceedsPaymentMethodCap)

  const isValid =
    (!hasPaymentMethod || paymentMethod !== '') &&
    !exceedsPaymentMethodCap &&
    tickets.every(
      (t) =>
        (!hasGameType || !isGameTypeRequired || t.gameType !== '') &&
        isBarcodeValid(t) &&
        (!hasPrizeAmount || !isPrizeAmountRequired || t.prizeAmount !== ''),
    )

  const handleSubmit = useCallback(() => {
    if (!isValid) {
      setAttemptedSubmit(true)
      return
    }

    const formattedTickets = tickets.map((t) => ({
      prizeAmount: Number(t.prizeAmount),
      gameType: t.gameType,
      barcodeNumber: t.barcodeNumber,
    }))

    const data: Record<string, unknown> = { tickets: formattedTickets }
    if (hasPaymentMethod) {
      data.paymentMethod = paymentMethod
    }

    onSubmit(data)
  }, [isValid, tickets, paymentMethod, hasPaymentMethod, onSubmit])

  return (
    <section className={CLASS_NAME}>
      <h2 className={`${CLASS_NAME}__section-title`}>Ticket Details</h2>
      <p className={`${CLASS_NAME}__disclaimer`}>Altering tickets subject to 5 years in prison.</p>

      <section className={`${CLASS_NAME}__field`}>
        <span id={`${fieldId}-ticket-count`} className={`${CLASS_NAME}__label`}>
          Number of Tickets
        </span>
        {claimReservationId ? (
          <p className={`${CLASS_NAME}__static-value`}>{numberOfTickets}</p>
        ) : (
          <Selector<number>
            ariaLabelledBy={`${fieldId}-ticket-count`}
            placeHolderText=""
            value={`${numberOfTickets}`}
            selectables={Array.from({ length: 15 }, (_, i) => ({
              value: i + 1,
              label: `${i + 1}`,
            }))}
            onValueChange={handleNumberOfTicketsChange}
          />
        )}
      </section>

      {hasPaymentMethod && (
        <section className={`${CLASS_NAME}__field`}>
          <span id={`${fieldId}-payment-method`} className={`${CLASS_NAME}__label`}>
            Payment Method *
          </span>
          <Selector<string>
            ariaLabelledBy={`${fieldId}-payment-method`}
            placeHolderText="Select Payment Method..."
            value={paymentMethod || undefined}
            selectables={paymentMethodOptions.map((opt) => ({
              value: opt,
              label: opt,
            }))}
            onValueChange={setPaymentMethod}
          />
          {paymentMethodDescriptions[paymentMethod] && (
            <p className={`${CLASS_NAME}__payment-method-description`}>
              {paymentMethodDescriptions[paymentMethod]}
            </p>
          )}
          {attemptedSubmit && paymentMethod === '' && (
            <span className={`${CLASS_NAME}__field-error`}>Payment Method is required</span>
          )}
        </section>
      )}

      {tickets.map((ticket, index) => {
        const headingId = `${fieldId}-ticket-heading-${index}`
        const showHeading = numberOfTickets > 1
        const groupName = ticketGroupName(index, headingId, showHeading)

        return (
          <section
            key={index}
            className={`${CLASS_NAME}__ticket`}
            role="group"
            aria-label={groupName.label}
            aria-labelledby={groupName.labelledBy}>
            {showHeading && (
              <h3 id={headingId} className={`${CLASS_NAME}__ticket-header`}>
                Ticket {index + 1} of {numberOfTickets}
              </h3>
            )}

            {hasPrizeAmount && (
              <section className={`${CLASS_NAME}__field`}>
                <label htmlFor={`${fieldId}-prize-${index}`} className={`${CLASS_NAME}__label`}>
                  Prize Amount{isPrizeAmountRequired ? ' *' : ''}
                </label>
                <section className={`${CLASS_NAME}__prize-input-wrapper`}>
                  <span className={`${CLASS_NAME}__prize-prefix`}>$</span>
                  <input
                    id={`${fieldId}-prize-${index}`}
                    type="text"
                    inputMode="decimal"
                    value={formatPrizeDisplay(ticket.prizeAmount)}
                    placeholder="Enter Prize Amount, e.g. 600"
                    onChange={(e) => handlePrizeChange(index, e.target.value)}
                    className={`${CLASS_NAME}__input-control ${CLASS_NAME}__prize-input`}
                  />
                </section>
                {ticketExceedsPaymentMethodCap(ticket) && (
                  <span className={`${CLASS_NAME}__field-error`}>
                    {paymentMethod} is not available for prizes over $
                    {paymentMethodCap.toLocaleString()}
                  </span>
                )}
                {attemptedSubmit && isPrizeAmountRequired && ticket.prizeAmount === '' && (
                  <span className={`${CLASS_NAME}__field-error`}>Prize Amount is required</span>
                )}
              </section>
            )}

            {hasGameType && (
              <section className={`${CLASS_NAME}__field`}>
                <span className={`${CLASS_NAME}__game-type-label ${CLASS_NAME}__label`}>
                  <span id={`${fieldId}-game-type-${index}`}>
                    Game Type{isGameTypeRequired ? ' *' : ''}
                  </span>
                  <button
                    type="button"
                    className={`${CLASS_NAME}__info-button`}
                    onClick={() => setShowGameTypeHelp(true)}
                    aria-label="Game Type Help">
                    ?
                  </button>
                </span>
                <Selector<string>
                  ariaLabelledBy={`${fieldId}-game-type-${index}`}
                  placeHolderText="Select Game Type..."
                  value={ticket.gameType || undefined}
                  selectables={gameTypeOptions.map((opt) => ({
                    value: opt,
                    label: opt,
                  }))}
                  onValueChange={(val) => {
                    updateTicket(index, 'gameType', val)
                    updateTicket(index, 'barcodeNumber', '')
                    updateTicket(index, 'selectedFormatIndex', 0)
                  }}
                />
                {attemptedSubmit && isGameTypeRequired && ticket.gameType === '' && (
                  <span className={`${CLASS_NAME}__field-error`}>Game Type is required</span>
                )}
              </section>
            )}

            {hasBarcodeNumber && (!hasGameType || ticket.gameType) && (
              <section className={`${CLASS_NAME}__field`}>
                <label htmlFor={`${fieldId}-barcode-${index}`} className={`${CLASS_NAME}__label`}>
                  Barcode Number{isBarcodeRequired ? ' *' : ''}
                </label>
                {isMultiFormat(ticket.gameType) ? (
                  <section className={`${CLASS_NAME}__barcode-formats`}>
                    <p className={`${CLASS_NAME}__barcode-format-hint`}>
                      Fill out the field that matches your ticket&apos;s barcode pattern.
                    </p>
                    {getBarcodeFormats(ticket.gameType).map((f, fIdx) => {
                      const isSelected = ticket.selectedFormatIndex === fIdx
                      return (
                        <label key={fIdx} className={`${CLASS_NAME}__barcode-format-row`}>
                          {/* The selected row replaces its pattern text with the input, so the radio names itself after the pattern it chooses. */}
                          <input
                            type="radio"
                            name={`barcode-format-${index}`}
                            aria-label={f.placeholder}
                            checked={isSelected}
                            onChange={() => {
                              updateTicket(index, 'selectedFormatIndex', fIdx)
                              updateTicket(index, 'barcodeNumber', '')
                            }}
                            className={`${CLASS_NAME}__barcode-format-radio`}
                          />
                          {isSelected ? (
                            <input
                              id={`${fieldId}-barcode-${index}`}
                              type="text"
                              autoFocus
                              value={ticket.barcodeNumber}
                              placeholder={f.placeholder}
                              onChange={(e) => {
                                const formatted = formatBarcode(e.target.value, f.placeholder)
                                updateTicket(index, 'barcodeNumber', formatted)
                              }}
                              maxLength={f.placeholder.length}
                              className={`${CLASS_NAME}__barcode-format-input`}
                            />
                          ) : (
                            <span className={`${CLASS_NAME}__barcode-format-label`}>
                              {f.placeholder}
                            </span>
                          )}
                        </label>
                      )
                    })}
                  </section>
                ) : (
                  <input
                    id={`${fieldId}-barcode-${index}`}
                    type="text"
                    value={ticket.barcodeNumber}
                    placeholder={
                      getFirstFormat(ticket.gameType)?.placeholder ?? 'Enter Barcode Number'
                    }
                    onChange={(e) => {
                      const format = getFirstFormat(ticket.gameType)
                      const formatted = format?.placeholder
                        ? formatBarcode(e.target.value, format.placeholder)
                        : e.target.value
                      updateTicket(index, 'barcodeNumber', formatted)
                    }}
                    maxLength={getFirstFormat(ticket.gameType)?.placeholder?.length}
                    className={`${CLASS_NAME}__input-control`}
                  />
                )}
                {attemptedSubmit && !isBarcodeValid(ticket) && (
                  <span className={`${CLASS_NAME}__field-error`}>
                    {ticket.barcodeNumber === ''
                      ? 'Barcode Number is required'
                      : 'Barcode Number does not match the expected format'}
                  </span>
                )}
              </section>
            )}
          </section>
        )
      })}

      <GameTypeHelpModal open={showGameTypeHelp} onClose={() => setShowGameTypeHelp(false)} />

      <FormFloatingFooter>
        <section className="floating-footer__buttons">
          <PreviousButton show={showBack} onClick={onBack} />
          <Button className="floating-footer__next-button" onClick={handleSubmit}>
            Next
          </Button>
        </section>
      </FormFloatingFooter>
    </section>
  )
}
